import type { ChangeEvent, KeyboardEvent, MouseEvent } from 'react';

/**
 * I-2317 (`busyKeepsFocus.guard.test.ts`): a control keeps the keyboard focus while its work runs.
 * Natively `disabled`, the control the person just used — the Save they pressed, the select they
 * changed, the field they pressed Enter in — left the tab order, and focus fell to the page's body.
 *
 * While `busy`, a control is instead `aria-disabled` and `aria-busy`, and takes nothing: a press is
 * cancelled before the caller's `onClick` or a form's submit sees it, a field takes no change. It is
 * marked `data-busy`, the one mark a busy control is dimmed by (`data-[busy]:opacity-50`; `Button`
 * dims by it itself) — never `aria-busy`, which a pending gate sets on a control it leaves undimmed
 * (`gateControl.ts`). A control disabled for its own reasons (`disabled`: nothing to save, a box not
 * ticked) stays natively disabled: nothing is pending there.
 *
 * The pressed `Button` itself takes `loading` instead, which does the same and shows its wait.
 */

type BusyMarksType = {
  'aria-disabled'?: true;
  'aria-busy'?: true;
  'data-busy'?: true;
};

const BUSY: BusyMarksType = { 'aria-disabled': true, 'aria-busy': true, 'data-busy': true };

/** A press refused — a click, and Enter or Space, which a button turns into a click. */
function refusePress(event: MouseEvent): void {
  event.preventDefault();
  event.stopPropagation();
}

/** A change a busy field takes: nothing is written. */
function refuseChange(): void {}

/** A select that must not open: pressing it, or any key but Tab, does nothing. */
function refuseOpen(event: MouseEvent): void {
  event.preventDefault();
}
function refuseKeysButTab(event: KeyboardEvent): void {
  if (event.key !== 'Tab') event.preventDefault();
}

type BusyControlPropsType = BusyMarksType & {
  disabled: boolean;
  onClickCapture?: (event: MouseEvent) => void;
};

/** What a `button` or `Button` spreads that is busy while `busy`, beside any of its own `disabled`. */
export function busyControlProps(
  busy: boolean,
  { disabled = false }: { disabled?: boolean } = {},
): BusyControlPropsType {
  if (disabled || !busy) return { disabled };
  return { disabled: false, ...BUSY, onClickCapture: refusePress };
}

type BusyFieldPropsType<E> = BusyMarksType & {
  disabled: boolean;
  readOnly?: true;
  onChange?: (event: E) => void;
  onClickCapture?: (event: MouseEvent) => void;
  onMouseDown?: (event: MouseEvent) => void;
  onKeyDown?: (event: KeyboardEvent) => void;
};

/**
 * What a field spreads that is busy while `busy` — as a refused field (`gateFieldProps`), it stays
 * readable and in the tab order and takes no change:
 *
 * - `text` (input, textarea): `readOnly`;
 * - `select`: it does not open — a press and every key but Tab are ignored;
 * - `toggle` (checkbox, radio): the click that would flip it is ignored.
 *
 * ⚠️ The field's `onChange` goes through here and comes back as a no-op while busy, so a way in that
 * list did not foresee (a phone's native picker) still writes nothing.
 */
export function busyFieldProps<E = ChangeEvent<HTMLElement>>(
  busy: boolean,
  kind: 'text' | 'select' | 'toggle',
  { disabled = false, onChange }: { disabled?: boolean; onChange?: (event: E) => void } = {},
): BusyFieldPropsType<E> {
  if (disabled || !busy) return onChange ? { disabled, onChange } : { disabled };
  const held: BusyFieldPropsType<E> = { disabled: false, ...BUSY, onChange: refuseChange };
  if (kind === 'text') return { ...held, readOnly: true };
  if (kind === 'select') return { ...held, onMouseDown: refuseOpen, onKeyDown: refuseKeysButTab };
  return { ...held, onClickCapture: refusePress };
}

type BusyFieldsetPropsType = BusyMarksType & {
  disabled: boolean;
  onChangeCapture?: (event: ChangeEvent<HTMLElement>) => void;
  onClickCapture?: (event: MouseEvent) => void;
};

/**
 * What a `fieldset` spreads that is busy while `busy`: its fields stay in the tab order, so the one
 * the person pressed Enter in keeps the focus, and take nothing. A change is stopped on its way to
 * the field's own `onChange`, so a controlled field keeps its value; a press (a button, a checkbox)
 * is refused. A fieldset disabled for its own reasons (`disabled`: the form is done) stays natively
 * disabled.
 */
export function busyFieldsetProps(
  busy: boolean,
  { disabled = false }: { disabled?: boolean } = {},
): BusyFieldsetPropsType {
  if (disabled || !busy) return { disabled };
  return {
    disabled: false,
    ...BUSY,
    onChangeCapture: (event) => event.stopPropagation(),
    onClickCapture: refusePress,
  };
}
